import { Col, Row } from '@advanza/grid'
import { SelectSimple } from '@advanza/input'
import { OptionStatus } from '@advanza/types/Option'
import { QuestionStatus } from '@advanza/types/Question'
import { Divider, Icon, PreIcon } from '@advanza/ui'
import { QuestionType } from '@trustoo/enums/QuestionType'
import { deleteQuestion, saveQuestion } from 'actions/services'
import Card from 'components/Card'
import CheckButton from 'components/CheckButton'
import BagUse from 'components/services/BagUse'
import EntityComponent from 'components/services/EntityComponent'
import Option from 'components/services/Option'
import TranslateButton from 'components/TranslateButton'
import SafeButton from 'components/ui/SafeButton'
import EntityComponentContainer from 'containers/EntityComponentContainer'
import { Fragment } from 'react'
import style from './questionEntity.module.css'

export const DEFAULT_DISPLAY_EMOJI = '📋'

class QuestionEntity extends EntityComponent {
    constructor(props) {
        super(props)

        this.isTouched = this.isTouched.bind(this)
        this.getAllServiceOptionsForQuestionRules =
            this.getAllServiceOptionsForQuestionRules.bind(this)
        this.highlightQuestionsSubjectToRules = this.highlightQuestionsSubjectToRules.bind(this)
        this.checkQuestionRulesAreOrdered = this.checkQuestionRulesAreOrdered.bind(this)
        this.checkRadioHasSingleDefault = this.checkRadioHasSingleDefault.bind(this)
        this.changeOrder = this.changeOrder.bind(this)
        this.addOption = this.addOption.bind(this)
        this.toggleShowHiddenOptions = this.toggleShowHiddenOptions.bind(this)
        this.optionCompMap = {}
        this.state = {
            showHiddenOptions: false,
        }
    }

    getAllServiceOptionsForQuestionRules() {
        const { serviceEntities, service, index } = this.props

        const selectOptions = []
        const questionsOrder = service.questions
        service.questions.forEach((id) => {
            if (
                questionsOrder.indexOf(id) < index &&
                [OptionStatus.ACTIVE, OptionStatus.NEW].includes(
                    serviceEntities.questions[id].status,
                )
            ) {
                const question = serviceEntities.questions[id]
                serviceEntities.questions[id].options.forEach((id) => {
                    const option = serviceEntities.options[id]
                    const isNew =
                        question.status === QuestionStatus.NEW || option.status === OptionStatus.NEW
                    selectOptions.push({
                        title: `${isNew ? '[NEW] ' : ''}${question.question} - ${option.value}`,
                        value: id,
                        queryField: `${question.question} ${option.value}`,
                    })
                })
            }
        })

        return selectOptions
    }

    componentDidMount() {
        const { entity } = this.props

        if (entity._openToggle) {
            this.ref && this.ref.scrollIntoView()
        }

        if (entity.status !== QuestionStatus.ACTIVE) {
            this.toggleShowHiddenOptions(true)
        }
    }

    checkRadioHasSingleDefault() {
        const { entity: question, serviceEntities } = this.props

        if (question.type !== QuestionType.RADIO) {
            return true
        }

        const defaultCount = question.options.filter(
            (id) => serviceEntities.options[id].is_default === true,
        ).length

        // a radio question should not have multiple default values
        return defaultCount <= 1
    }

    checkQuestionRulesAreOrdered() {
        const { service, serviceEntities } = this.props
        const order = service.questions

        for (let i = 0; i < order.length; i++) {
            const id = order[i]
            const thisQuestionIndex = order.indexOf(id)
            const question = serviceEntities.questions[id]
            for (const key in question.question_rules) {
                const option = serviceEntities.options[question.question_rules[key]]
                const questionIndex = order.indexOf(option.question_id)
                if (questionIndex > thisQuestionIndex) {
                    return false
                }
            }
        }

        return true
    }

    validate(showErrors = false, saveAttempted = null) {
        const questionIsValid = super.validate(showErrors, saveAttempted)
        let optionsAreValid = true

        Object.values(this.optionCompMap).forEach((optionComp) => {
            if (optionComp && !optionComp.validate(showErrors, saveAttempted)) {
                optionsAreValid = false
            }
        })

        const orderIsValid = this.checkQuestionRulesAreOrdered()
        const radioDefaultsValid = this.checkRadioHasSingleDefault()
        return questionIsValid && optionsAreValid && orderIsValid && radioDefaultsValid
    }

    editFields() {
        const { entity: question, typeMap, serviceEntities, service } = this.props

        // A moment picker is allowed to be combined with a day/month picker, but not as availability
        // question, as there should not be two types of availability questions in the wizard.
        const disableMomentPickerAvailability =
            question.type === 'v' &&
            service.questions.some((questionId) =>
                ['d', 'm'].includes(serviceEntities.questions[questionId]?.type),
            )

        return {
            question: {
                type: 'text',
                textArea: question.type === 's',
                autoFocus:
                    question._openToggle && !(question.question && question.question.length > 2),
                msg: "Question e.g. 'What services do you require?'",
                validator: (val) => typeof val === 'string' && val.length > 1 && val.length < 512,
                errorMsg: 'Text must be between 2 and 511 characters',
            },
            question_name_pro: {
                type: 'text',
                msg: "Question PRO e.g. 'What services do you provide?'",
                validator: (val) =>
                    !question.filterable || question.type === 's'
                        ? true
                        : typeof val === 'string' && val.length > 1 && val.length < 155,
                errorMsg: 'Text must be between 2 and 154 characters',
                isHidden: !question.filterable || question.type === 's',
            },
            question_name_filter: {
                type: 'text',
                msg: "Short e.g. 'Services'",
                isHidden: question.type === 's',
                validator: (val) =>
                    question.type === 's' ||
                    (typeof val === 'string' && val.length > 1 && val.length < 155),
                errorMsg: 'Text must be between 2 and 154 characters',
            },
            display_emoji: {
                type: 'text',
                msg: 'Emoji prefix (e.g. 🍆)',
                validator: (val) => (val ? val.length < 17 : true),
                errorMsg: 'Text must be 16 characters or less (or empty)',
                valueFormatter: (value) => value ?? DEFAULT_DISPLAY_EMOJI,
                maxLength: 16,
                className: style.emojiInput,
            },
            linked_to: {
                type: 'select',
                placeholder: 'Linked to',
                options: [
                    { name: 1, value: 1 },
                    { name: 2, value: 2 },
                    { name: 3, value: 3 },
                ],
            },
            description: {
                type: 'text',
                msg: '*optional* Description (the subline of the question)',
                validator: (val) => (val ? val.length > 1 && val.length < 100 : true),
                errorMsg: 'Text must be between 2 and 99 characters (or empty)',
            },
            filterable: {
                type: 'boolean',
                msg: 'Filterable',
                isHidden:
                    ['1', '2'].indexOf(question.type) === -1 ||
                    question.after_conversion ||
                    question.sea_page_visibility !== 0 ||
                    (question.type === 'v' && !service.enable_opening_hours), // moment picker need opening hours to be filterable
            },
            is_availability: {
                type: 'boolean',
                msg: (
                    <PreIcon icon="event" fontSize={17}>
                        Availability
                    </PreIcon>
                ),
                // A question should not be allowed as availability if:
                // - it's an after conversion question
                // - the current question is a moment picker and the service already has a day or month picker (which can be the availability question)
                isHidden: question.after_conversion || disableMomentPickerAvailability,
            },
            is_privacy_sensitive: {
                type: 'boolean',
                msg: 'Privacy sensitive',
            },
            house_number_required: {
                type: 'boolean',
                msg: 'House number required',
                // Only location questions have an address that can require a house number
                isHidden: question.type !== QuestionType.GOOGLE_MAPS_PLACE,
            },
            demand_only: {
                type: 'boolean',
                msg: 'Only visible for Demand',
                isHidden: question.type === 'f', // File questions don't support demand_only filtering
            },
            is_intention_to_hire: {
                type: 'boolean',
                msg: 'Intention to hire',
                // Only a single-answer question can express one intention level
                isHidden: question.type !== QuestionType.RADIO,
            },
            sea_page_visibility: {
                type: 'select',
                placeholder: 'SEA',
                options: [
                    { title: 'Default', value: 0 },
                    { title: 'SEA-only', value: 1 },
                    { title: 'SEA-hidden', value: 2 },
                ],
                isHidden: question.filterable, // Only show for non-filterable questions
                title: 'SEA page visibility: Configure how this question appears on SEA landing pages',
            },
            optional: {
                isHidden: question.filterable,
                type: 'boolean',
                msg: 'Optional',
                disabled: question.after_conversion, // not allowed toggle the optional to false if the after conversion is true
            },
            after_conversion: {
                type: 'boolean',
                msg: (
                    <PreIcon icon="text_select_move_forward_word" fontSize={17}>
                        After conversion
                    </PreIcon>
                ),
                isHidden:
                    question.filterable ||
                    ['d', 'm'].includes(question.type) ||
                    question.is_availability,
                onChange: (e) => {
                    const { value } = e.target
                    if (value) {
                        // after conversion questions are always optional, so set both to true
                        this.onChangeEntity({
                            after_conversion: true,
                            optional: true,
                        })
                    } else {
                        this.onChangeEntity({
                            after_conversion: false,
                        })
                    }
                },
            },
            default_option: {
                type: 'select',
                placeholder: 'default option',
                options: question.options.map((id) => {
                    return {
                        value: id,
                        title: serviceEntities.options[id].value,
                    }
                }),
            },
            placeholder: {
                isHidden:
                    ['2', '1', '5', '0', '6', '7', '9', 'd', 'o'].indexOf(question.type) === -1,
                type: 'text',
                msg: 'placeholder',
            },
            type: {
                type: 'select',
                options: Object.keys(typeMap)
                    .map((key) => ({
                        type: key,
                        ...typeMap[key],
                    }))
                    .filter((item) => {
                        if (item.type === question.type) {
                            // the type that is already selected should always be visible
                            return true
                        }

                        if (item.deprecated) {
                            // hide question types that are deprecated
                            return false
                        }

                        if (item.required && !service[item.required]) {
                            // hide question types that are not enabled for the service
                            return false
                        }

                        return true
                    })
                    .map((item) => {
                        const invalidType =
                            (item.required && !service[item.required]) || item.deprecated

                        return {
                            title: (
                                <PreIcon
                                    icon={item.icon}
                                    overflowFix
                                    red={invalidType}
                                    className={invalidType ? style.invalidType : ''}>
                                    {item.name}{' '}
                                    {invalidType &&
                                        `[${item.deprecated ? 'DEPRECATED' : 'NOT ENABLED'}]`}
                                </PreIcon>
                            ),
                            value: item.type,
                        }
                    }),
                validators: [
                    {
                        check: (value) =>
                            ![QuestionType.CHECKBOX, QuestionType.RADIO].includes(value) ||
                            question.options.length > 0,
                        errorMsg: 'This question type requires at least one option',
                    },
                    {
                        check: () => this.checkRadioHasSingleDefault(),
                        errorMsg: 'Radio questions cannot have more than one default wizard answer',
                    },
                ],
                className: style.questionType,
                onChange: (e) => {
                    const updates = { type: e.target.value }
                    if (updates.type === 'f' && question.demand_only) {
                        // Edge case: if the question is currently demand_only and the type is changed to file, clear the demand_only flag
                        updates.demand_only = false
                    }
                    if (updates.type !== QuestionType.RADIO && question.is_intention_to_hire) {
                        // The toggle is hidden for non-radio types, so clear it rather than leave a flag nobody can see
                        updates.is_intention_to_hire = false
                    }
                    this.onChangeEntity(updates)
                },
            },
            status: {
                type: 'select',
                placeholder: 'Status',
                options: [
                    { title: <PreIcon name="fiber_new">New</PreIcon>, value: QuestionStatus.NEW },
                    {
                        title: <PreIcon name="visibility">Active</PreIcon>,
                        value: QuestionStatus.ACTIVE,
                    },
                    {
                        title: <PreIcon name="visibility_off">Hidden</PreIcon>,
                        value: QuestionStatus.HIDDEN,
                    },
                ],
                onBeforeChange: (value) =>
                    value !== QuestionStatus.ACTIVE && this.props.toggleShowHiddenQuestions(true),
                className: style.questionStatus,
            },
            question_rules: {
                type: 'select',
                icon: 'shuffle',
                placeholder: 'Only show question when:',
                useSearch: true,
                options: this.getAllServiceOptionsForQuestionRules(),
                multiple: true,
                value: question.question_rules,
                onValueChange: (values) =>
                    this.onChangeEntity({
                        question_rules: values.map((item) => item.value),
                    }),
                validator: this.checkQuestionRulesAreOrdered,
                errorMsg:
                    '"Only show question when" rules may only depend on options of earlier questions',
            },
            calculator_value: {
                type: 'number',
                msg: '€ calc. value',
                isHidden: question.type !== '7',
                title: 'If left empty, this question will disable the calculator when a value > 0 is entered',
            },
        }
    }

    isTouched() {
        const { entity: question, serviceEntities } = this.props
        const questionIsTouched = question._isTouched
        const optionsAreTouched =
            question.options.filter((id) => serviceEntities.options[id]._isTouched).length > 0
        const { bag_uses = {} } = serviceEntities
        const bagUse = bag_uses[question.bag_use || `-${question.question_id}`]
        const bagUseIsTouched = bagUse && bagUse._isTouched

        return questionIsTouched || optionsAreTouched || bagUseIsTouched
    }

    changeOrder(upOrDown, e) {
        const { entity: question, changeQuestionOrder, index } = this.props
        changeQuestionOrder(question, upOrDown)

        // change the entity so that the save button will appear. It doesn't mean anything
        this.onChangeEntity({ '|index': index })
    }

    highlightQuestionsSubjectToRules(hover) {
        const { entity: question, setHighlight, serviceEntities } = this.props
        const highlights = {}
        question.question_rules.forEach((optionId) => {
            const option = serviceEntities.options[optionId]
            highlights[option.question_id] = (highlights[option.question_id] || []).concat(
                <span className="label">{option.value}</span>,
            )
        })

        if (hover) {
            setHighlight(highlights)
        } else {
            setHighlight({})
        }
    }

    toggleShowHiddenOptions(show = null) {
        this.setState({
            showHiddenOptions: show === null ? this.state.showHiddenOptions !== true : show,
        })
    }

    addOption() {
        const { entity: question, addOption } = this.props
        addOption(question.question_id)
    }

    save(...params) {
        const { addToast } = this.props

        super.save(params).catch((response) => {
            let errorMsg

            if (response instanceof Error) {
                // client side error
                errorMsg = response.message
            } else {
                // server side error
                const { status, error, ...rest } = response
                errorMsg = error && error !== 'fields' ? error : JSON.stringify(rest, null, 2)

                // only handle 'fields' errors for the options, for the question it's already handled
                if (error === 'fields' && rest.options) {
                    Object.entries(rest.options).forEach(([optionId, optionFieldErrors]) => {
                        const errors = {}

                        Object.entries(optionFieldErrors).forEach(([field, fieldError]) => {
                            errors[field] = JSON.stringify(fieldError) || true
                        })

                        this.onChangeOtherEntity({
                            store: 'services',
                            name: 'options',
                            key: optionId,
                            dontTouch: true,
                            diff: { _errors: errors, _saveAttempted: true },
                        })
                    })
                }
            }

            addToast({
                closeBtn: false,
                msg: `**Error**\nChanges were not saved\n${errorMsg}`,
            })
        })
    }

    render() {
        const { entity: question, serviceEntities, service, addOptionImage } = this.props
        const { showHiddenOptions } = this.state
        const showOptions = question.type < 3

        return (
            <>
                <Row middle="xs" className={style.sticky}>
                    <Col x>{this.renderInput('status')}</Col>
                    <Col x>{this.renderInput('type')}</Col>
                    {this.isVisible('is_availability') && (
                        <Col x>{this.renderInput('is_availability')}</Col>
                    )}
                    {this.isVisible('filterable') && <Col x>{this.renderInput('filterable')}</Col>}
                    {this.isVisible('optional') && <Col x>{this.renderInput('optional')}</Col>}
                    {this.isVisible('after_conversion') && (
                        <Col x>{this.renderInput('after_conversion')}</Col>
                    )}
                    <Col x>{this.renderInput('is_privacy_sensitive')}</Col>
                    {this.isVisible('house_number_required') && (
                        <Col x>{this.renderInput('house_number_required')}</Col>
                    )}
                    <Col x>{this.renderInput('demand_only')}</Col>
                    {this.isVisible('is_intention_to_hire') && (
                        <Col x>{this.renderInput('is_intention_to_hire')}</Col>
                    )}
                    {this.isVisible('sea_page_visibility') && (
                        <Col x>{this.renderInput('sea_page_visibility')}</Col>
                    )}
                    <Col x style={{ marginRight: 0, marginLeft: 'auto' }}>
                        <Row middle="xs">
                            <Col x>
                                <SafeButton
                                    action={this.delete}
                                    cancelText={false}
                                    className="plain c-red"
                                    confirmMessage="Confirm delete"
                                    buttonText={<Icon>delete_forever</Icon>}
                                />
                            </Col>
                            <Col x>
                                <button
                                    onClick={() => this.save()}
                                    className="btn cta"
                                    disabled={!this.isTouched()}>
                                    {question._saving ? 'saving..' : 'Save'}
                                </button>
                            </Col>
                        </Row>
                    </Col>
                </Row>
                <Row center="xs" className={style.questionRow}>
                    <Col xs={6}>
                        <Card header="Question">
                            <Row middle="xs" style={{ flexWrap: 'nowrap' }}>
                                <Col style={{ maxWidth: 478 }}>
                                    {this.renderInput('question_rules')}
                                </Col>
                                {this.isVisible('calculator_value') && (
                                    <Col x>{this.renderInput('calculator_value')}</Col>
                                )}
                            </Row>
                            <Divider m />
                            <div
                                ref={(el) => {
                                    this.ref = el
                                }}>
                                {' '}
                            </div>
                            <TranslateButton
                                text={question.question ?? ''}
                                targetLanguage="en"
                                showWhenEmpty
                                stacked>
                                {this.renderInput('question')}
                            </TranslateButton>
                            <Divider m />
                            {question.type === 's' && (
                                <>
                                    <SelectSimple
                                        value={question.linked_to}
                                        icon={<Icon name="link" />}
                                        placeholder="Linked to"
                                        onChange={(e) =>
                                            this.onChangeEntity({ linked_to: e.target.value })
                                        }
                                        options={[
                                            {
                                                name: '-',
                                                value: null,
                                            },
                                        ].concat(
                                            service.questions
                                                .filter((id) => {
                                                    const question = serviceEntities.questions[id]
                                                    return question.type !== 's'
                                                })
                                                .map((id) => {
                                                    const question = serviceEntities.questions[id]
                                                    return {
                                                        name: question.question,
                                                        value: id,
                                                    }
                                                }),
                                        )}
                                    />
                                    <Divider m />
                                </>
                            )}
                            {this.isVisible('question_name_pro') && (
                                <>
                                    <TranslateButton
                                        text={question.question_name_pro ?? ''}
                                        targetLanguage="en"
                                        showWhenEmpty
                                        stacked>
                                        {this.renderInput('question_name_pro')}
                                    </TranslateButton>
                                    <Divider m />
                                </>
                            )}

                            {this.isVisible('question_name_filter') && (
                                <>
                                    <TranslateButton
                                        text={question.question_name_filter ?? ''}
                                        targetLanguage="en"
                                        showWhenEmpty
                                        stacked>
                                        {this.renderInput('question_name_filter')}
                                    </TranslateButton>
                                    <Divider m />
                                </>
                            )}

                            <TranslateButton
                                text={question.description ?? ''}
                                targetLanguage="en"
                                showWhenEmpty
                                stacked>
                                {this.renderInput('description')}
                            </TranslateButton>
                            <Divider m />

                            {this.renderInput('display_emoji')}
                            <Divider m />

                            {this.isVisible('placeholder') && this.renderInput('placeholder')}

                            {question.type === 'l' && (
                                <BagUse entityId={question.bag_use || `-${question.question_id}`} />
                            )}
                        </Card>
                    </Col>
                    {showOptions && (
                        <Col style={{ minWidth: 500 }} xs={6} className="grow">
                            <Card header="options">
                                <Row middle="xs">
                                    <Col x>
                                        <CheckButton
                                            onChange={this.toggleShowHiddenOptions.bind(this, null)}
                                            checked={showHiddenOptions}
                                            msg="Show hidden"
                                        />
                                    </Col>
                                    <Col x>
                                        <button onClick={this.addOption}>
                                            <Icon>add</Icon>
                                        </button>
                                    </Col>
                                </Row>
                                <Divider m />
                                {question.options
                                    .sort((a, b) => {
                                        // Mirror the backend sort: order_value ASC with
                                        // option_id ASC as a deterministic tiebreaker so
                                        // Office and Supply Dashboard render the same order
                                        // even when admins haven't reordered options yet
                                        // (all default order_value=0). See TRUS-7613.
                                        const orderDiff =
                                            serviceEntities.options[a].order_value -
                                            serviceEntities.options[b].order_value
                                        return orderDiff === 0 ? a - b : orderDiff
                                    })
                                    .map((id, i) => {
                                        if (
                                            serviceEntities.options[id].status !==
                                                OptionStatus.ACTIVE &&
                                            !showHiddenOptions
                                        ) {
                                            return null
                                        }
                                        return (
                                            <Fragment key={id}>
                                                <Option
                                                    ref={(el) => (this.optionCompMap[id] = el)}
                                                    toggleShowHidden={this.toggleShowHiddenOptions}
                                                    entityId={id}
                                                    index={i + 1}
                                                    service={service}
                                                    addOptionImage={addOptionImage}
                                                    option={serviceEntities.options[id]}
                                                    questionType={question.type}
                                                    questionFilterable={question.filterable}
                                                    questionIsIntentionToHire={
                                                        question.is_intention_to_hire
                                                    }
                                                />
                                                <Divider name="border" l />
                                            </Fragment>
                                        )
                                    })}
                            </Card>
                        </Col>
                    )}
                </Row>
            </>
        )
    }
}

export default EntityComponentContainer(QuestionEntity, {
    store: 'services',
    name: 'questions',
    saveFunc: saveQuestion,
    deleteFunc: deleteQuestion,
})
